import { Effect } from "effect";
import {
  type RiskCategorizationOptions,
  type RiskTemplateMeasureLinkByTemplateRow,
  type RiskTemplateMeasureLinkRow,
  type RiskTemplateRow,
} from "@pima/web-api/client";
import { runIntentAsError } from "../run-intent";
import { getClient } from "./client";

// ===========================================================================
// admin-risk-templates — read-only here: the sync engine's
// copyTemplateDataToRiskInstance/copyMeasureTemplateLinksToRiskInstance seed
// a newly-created risk instance's tags/laws/attachments/measure-links from
// the template it was generated from.
// ===========================================================================

export async function getRiskTemplate(id: string): Promise<RiskTemplateRow> {
  const client = await getClient();
  const { data } = await runIntentAsError(
    client["admin-risk-templates"].getRiskTemplate({ urlParams: { id } }),
  );
  return data;
}

export async function listRiskTemplateMeasureLinks(
  templateId: string,
): Promise<readonly RiskTemplateMeasureLinkRow[]> {
  const client = await getClient();
  const { items } = await runIntentAsError(
    client["admin-risk-templates"].listRiskTemplateMeasureLinks({
      urlParams: { template_id: templateId },
    }),
  );
  return items;
}

// The sync engine's cache refresh needs the measure links of every cached
// risk template. Taking them one template at a time made the offline
// provisioning screen fire one request per template (hundreds, sequentially),
// so ids ride a POST body and come back tagged with their own template id.
export async function listRiskTemplateMeasureLinksByTemplates(
  riskTemplateIds: string[],
): Promise<readonly RiskTemplateMeasureLinkByTemplateRow[]> {
  if (riskTemplateIds.length === 0) return [];
  const client = await getClient();
  const { items } = await runIntentAsError(
    client["admin-risk-templates"].listRiskTemplateMeasureLinksByTemplates({
      payload: { risk_template_ids: riskTemplateIds },
    }),
  );
  return items;
}

// The sync engine's cache refresh resolves risk templates linked to a
// batch of cached questions by translation group, in one round trip — ids
// ride the POST body since the set can exceed a URL length limit.
export async function listRiskTemplatesByTranslationGroups(
  translationGroupIds: number[],
): Promise<readonly RiskTemplateRow[]> {
  if (translationGroupIds.length === 0) return [];
  const client = await getClient();
  const { items } = await runIntentAsError(
    client["admin-risk-templates"].listRiskTemplatesByTranslationGroups({
      payload: { translation_group_ids: translationGroupIds },
    }),
  );
  return items;
}

// The sync engine's add-risk modal caches every language of all seven
// risk-categorization reference tables (area/object/activity/factor/
// subfactor/source/mode) in one round trip so the dropdowns work offline.
export async function listRiskCategorizationOptions(): Promise<RiskCategorizationOptions> {
  const client = await getClient();
  const { data } = await runIntentAsError(
    client["admin-risk-templates"].listRiskCategorizationOptions(),
  );
  return data;
}
// ===========================================================================
// Risk authoring — admin-risk-templates / admin-risk-instances /
// admin-risk-actions / admin-measure-templates / admin-questionnaires /
// admin-location-risk-fields. Kept in one contiguous block so a parallel
// admin-area swap (clients/config, courses/documents) can append its own
// block below this one without touching these lines.
// ===========================================================================

export async function fetchRiskTemplates(params: {
  language?: "de-DE" | "en-US";
  status?: "draft" | "approved";
  search?: string;
  area?: readonly string[];
  object?: readonly string[];
  activity?: readonly string[];
  factor?: readonly string[];
  subfactor?: readonly string[];
  source?: readonly string[];
  limit: number;
  offset: number;
}): Promise<{ items: readonly RiskTemplateRow[]; total: number }> {
  const client = await getClient();
  return Effect.runPromise(
    client["admin-risk-templates"].listRiskTemplates({
      urlParams: {
        limit: params.limit,
        offset: params.offset,
        ...(params.language ? { language: params.language } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.search ? { search: params.search } : {}),
        ...(params.area?.length ? { area: params.area } : {}),
        ...(params.object?.length ? { object: params.object } : {}),
        ...(params.activity?.length ? { activity: params.activity } : {}),
        ...(params.factor?.length ? { factor: params.factor } : {}),
        ...(params.subfactor?.length ? { subfactor: params.subfactor } : {}),
        ...(params.source?.length ? { source: params.source } : {}),
      },
    }),
  );
}

export async function fetchRiskTemplate(
  id: string,
): Promise<RiskTemplateRow | null> {
  const client = await getClient();
  return Effect.runPromise(
    client["admin-risk-templates"].getRiskTemplate({ urlParams: { id } }).pipe(
      Effect.map(({ data }) => data),
      Effect.catchTag("RiskTemplateNotFound", () => Effect.succeed(null)),
    ),
  );
}

export async function createRiskTemplate(payload: {
  language: "de-DE" | "en-US";
  title: string;
  description: string;
  status?: "draft" | "approved";
  translation_group_id?: number;
  area_translation_group_id: number;
  object_translation_group_id: number;
  activity_translation_group_id: number;
  factor_translation_group_id: number;
  subfactor_translation_group_id: number;
  source_translation_group_id?: number;
  mode_translation_group_id?: number;
}): Promise<RiskTemplateRow> {
  const client = await getClient();
  const { data } = await Effect.runPromise(
    client["admin-risk-templates"].createRiskTemplate({
      payload: {
        language: payload.language,
        title: payload.title,
        description: payload.description,
        area_translation_group_id: payload.area_translation_group_id,
        object_translation_group_id: payload.object_translation_group_id,
        activity_translation_group_id: payload.activity_translation_group_id,
        factor_translation_group_id: payload.factor_translation_group_id,
        subfactor_translation_group_id: payload.subfactor_translation_group_id,
        status: payload.status ?? "draft",
        ...(payload.translation_group_id !== undefined
          ? { translation_group_id: payload.translation_group_id }
          : {}),
        ...(payload.source_translation_group_id !== undefined
          ? { source_translation_group_id: payload.source_translation_group_id }
          : {}),
        ...(payload.mode_translation_group_id !== undefined
          ? { mode_translation_group_id: payload.mode_translation_group_id }
          : {}),
      },
    }),
  );
  return data;
}

export async function updateRiskTemplate(payload: {
  id: string;
  title?: string;
  description?: string;
  status?: "draft" | "approved";
  area_translation_group_id?: number;
  object_translation_group_id?: number;
  activity_translation_group_id?: number;
  factor_translation_group_id?: number;
  subfactor_translation_group_id?: number;
  source_translation_group_id?: number | null;
  mode_translation_group_id?: number | null;
}): Promise<RiskTemplateRow> {
  const client = await getClient();
  const { data } = await Effect.runPromise(
    client["admin-risk-templates"].updateRiskTemplate({ payload }),
  );
  return data;
}

export async function deleteRiskTemplate(id: string): Promise<void> {
  const client = await getClient();
  await Effect.runPromise(
    client["admin-risk-templates"].deleteRiskTemplate({ payload: { id } }),
  );
}

export async function addRiskTemplateTag(params: {
  templateId: string;
  tagGroupId: number;
}): Promise<void> {
  const client = await getClient();
  await Effect.runPromise(
    client["admin-risk-templates"].addRiskTemplateTag({
      payload: {
        template_id: params.templateId,
        tag_group_id: params.tagGroupId,
      },
    }),
  );
}

export async function removeRiskTemplateTag(params: {
  templateId: string;
  tagGroupId: number;
}): Promise<void> {
  const client = await getClient();
  await Effect.runPromise(
    client["admin-risk-templates"].removeRiskTemplateTag({
      payload: {
        template_id: params.templateId,
        tag_group_id: params.tagGroupId,
      },
    }),
  );
}

export async function addRiskTemplateLaw(params: {
  templateId: string;
  lawGroupId: number;
}): Promise<void> {
  const client = await getClient();
  await Effect.runPromise(
    client["admin-risk-templates"].addRiskTemplateLaw({
      payload: {
        template_id: params.templateId,
        law_group_id: params.lawGroupId,
      },
    }),
  );
}

export async function removeRiskTemplateLaw(params: {
  templateId: string;
  lawGroupId: number;
}): Promise<void> {
  const client = await getClient();
  await Effect.runPromise(
    client["admin-risk-templates"].removeRiskTemplateLaw({
      payload: {
        template_id: params.templateId,
        law_group_id: params.lawGroupId,
      },
    }),
  );
}

export async function addRiskTemplateMeasureLink(params: {
  riskTemplateId: string;
  measureTemplateId: string;
}): Promise<void> {
  const client = await getClient();
  await Effect.runPromise(
    client["admin-risk-templates"].addRiskTemplateMeasureLink({
      payload: {
        risk_template_id: params.riskTemplateId,
        measure_template_id: params.measureTemplateId,
      },
    }),
  );
}

export async function removeRiskTemplateMeasureLink(params: {
  riskTemplateId: string;
  measureTemplateId: string;
}): Promise<void> {
  const client = await getClient();
  await Effect.runPromise(
    client["admin-risk-templates"].removeRiskTemplateMeasureLink({
      payload: {
        risk_template_id: params.riskTemplateId,
        measure_template_id: params.measureTemplateId,
      },
    }),
  );
}

export async function addRiskTemplateAttachment(params: {
  templateId: string;
  fileId: string;
}): Promise<void> {
  const client = await getClient();
  await Effect.runPromise(
    client["admin-risk-templates"].addRiskTemplateAttachment({
      payload: { template_id: params.templateId, file_id: params.fileId },
    }),
  );
}

export async function removeRiskTemplateAttachment(params: {
  templateId: string;
  fileId: string;
}): Promise<void> {
  const client = await getClient();
  await Effect.runPromise(
    client["admin-risk-templates"].removeRiskTemplateAttachment({
      payload: { template_id: params.templateId, file_id: params.fileId },
    }),
  );
}

// Retires every language variant of one risk-template translation group at
// once — the risk-center's draft-row delete action.
export async function deleteRiskTemplatesByTranslationGroup(
  translationGroupId: number,
): Promise<number> {
  const client = await getClient();
  const { data } = await Effect.runPromise(
    client["admin-risk-templates"].deleteRiskTemplatesByTranslationGroup({
      payload: { translation_group_id: translationGroupId },
    }),
  );
  return data.deleted;
}

// Approves every language variant of one risk-template translation group at
// once — the risk-center's draft-row approve action; a template's status
// stays in lockstep across its languages.
export async function approveRiskTemplatesByTranslationGroup(
  translationGroupId: number,
): Promise<number> {
  const client = await getClient();
  const { data } = await Effect.runPromise(
    client["admin-risk-templates"].approveRiskTemplatesByTranslationGroup({
      payload: { translation_group_id: translationGroupId },
    }),
  );
  return data.approved;
}
